/* The keyword rule, on the client side: ONE primary and up to THREE secondaries.
 *
 * A mirror of backend/lib/keyword-rule.js, and deliberately a small one. The
 * server is the authority: it caps the brief it assembles and the article it
 * generates, so a client that got this wrong could not smuggle a fourth
 * secondary past it. What the mirror buys is that the SEO panel can disable the
 * Add button at three and count "2 / 3" without a round trip, instead of
 * letting someone type a fifth keyword and having it vanish on save.
 *
 * backend/test/keyword-rule.test.js asserts the numbers here match the server's,
 * so the panel cannot drift into promising something the generator will not do.
 */

export const KEYWORD_RULE = Object.freeze({ primary: 1, secondary: 3 });

export const MAX_SECONDARY = KEYWORD_RULE.secondary;

export const RULE_SENTENCE =
  'One primary keyword and up to three secondaries. The primary owns the title, URL and opening; each secondary should earn a section of its own.';

const clean = (v) => String(v === null || v === undefined ? '' : v).trim();

/** Trimmed, non-empty, de-duplicated case-insensitively, order preserved. */
export function normaliseKeywords(list) {
  const seen = new Set();
  const out = [];
  for (const raw of (Array.isArray(list) ? list : [])) {
    const k = clean(raw);
    if (!k) continue;
    const key = k.toLowerCase();
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(k);
  }
  return out;
}

/** { secondary, spare }: the first three that are not the primary, and the rest. */
export function capSecondary(list, primary) {
  const p = clean(primary).toLowerCase();
  const all = normaliseKeywords(list).filter(k => !p || k.toLowerCase() !== p);
  return { secondary: all.slice(0, MAX_SECONDARY), spare: all.slice(MAX_SECONDARY) };
}

/**
 * What to tell someone looking at a count of secondary keywords. Only "over" is
 * a failure: one secondary still publishes, six is the problem the rule exists
 * to stop.
 */
export function secondaryAdvice(count) {
  const n = Number(count) || 0;
  if (n > MAX_SECONDARY) {
    return {
      state: 'over', ok: false, count: n,
      note: `${n} secondary keywords is over the limit of ${MAX_SECONDARY}. Anything past three is usually a second article competing with this one.`,
    };
  }
  if (n === MAX_SECONDARY) {
    return {
      state: 'on_target', ok: true, count: n,
      note: `On target: ${KEYWORD_RULE.primary} primary and ${MAX_SECONDARY} secondary keywords.`,
    };
  }
  if (n === 0) {
    return {
      state: 'empty', ok: true, count: n,
      note: `No secondary keywords yet. Aim for ${MAX_SECONDARY}, each covering a sub-intent of the primary.`,
    };
  }
  return {
    state: 'under', ok: true, count: n,
    note: `${n} of ${MAX_SECONDARY}. Add ${MAX_SECONDARY - n} more, each one earning a section of its own.`,
  };
}
